<template>
    <div class="component-gallery-with-enquiry">
        <div
            v-if="!isMobile"
            class="desktop"
        >
            <TransitionGroup
                :css="false"
                class="items"
                tag="div"
                @enter="onEnter"
                @after-enter="onAfterEnter"
            >
                <div
                    v-for="(item, index) in itemsDisplayed"
                    :key="item.slug"
                    :data-index="index"
                    class="item"
                    @click="openDesktopModal(item)"
                >
                    <CmsImage
                        :alt="item.caption"
                        :data="item.image"
                        fit="cover"
                        :is-eager="hasEagerImage && index < 6"
                        :sizes="[
                            'calc((100vw - 60px) / 2)',
                            [768, 'calc((100vw - 80px) / 3)'],
                            [1140, '367px'],
                        ]"
                    />

                    <div class="overlay">{{ item.caption }}</div>
                </div>
            </TransitionGroup>

            <div class="buttons">
                <ThButton
                    v-if="!areAllItemsDislpayed"
                    class="button-show-more"
                    color="var(--blue)"
                    @click="showMore"
                >
                    {{
                        data.loadMoreButtonText
                            ? data.loadMoreButtonText
                            : 'Show more'
                    }}
                </ThButton>

                <ThButton
                    class="button-show-more"
                    color="var(--blue)"
                    @click="handleNonModalCta"
                >
                    {{ data.buttonText ? data.buttonText : 'Design your own' }}
                </ThButton>
            </div>

            <TransitionModal>
                <Fixed
                    v-if="isDesktopModalOpen && currentItem"
                    @close="closeDesktopModal"
                >
                    <div class="desktop-modal">
                        <div class="image-wrapper">
                            <transition :name="desktopModalTransitionName">
                                <CmsImage
                                    :alt="currentItem.caption"
                                    :data="currentItem.image"
                                    fit="contain"
                                    is-eager
                                    :sizes="['65vw']"
                                />
                            </transition>
                        </div>

                        <div class="caption">
                            {{ currentItem?.caption }}
                        </div>

                        <ThButton
                            color="var(--white)"
                            type="bordered"
                            @click="handleModalCta"
                        >
                            {{
                                data.modalButtonText
                                    ? data.modalButtonText
                                    : 'Design a ring like this'
                            }}
                        </ThButton>

                        <button
                            class="button-close"
                            type="button"
                            @click="closeDesktopModal"
                        >
                            Close
                        </button>

                        <ButtonRound
                            :disabled="isAtStart"
                            class="button-navigation previous"
                            icon="angle-left"
                            :aria-label="t('catalogue.carousel.button_prev')"
                            @click="previous"
                        />

                        <ButtonRound
                            :disabled="isAtEnd"
                            class="button-navigation next"
                            icon="angle-right"
                            :aria-label="t('catalogue.carousel.button_next')"
                            @click="next"
                        />
                    </div>
                </Fixed>
            </TransitionModal>
        </div>

        <div
            v-else
            class="mobile"
        >
            <div class="carousel-container">
                <div
                    ref="containerMobile"
                    class="slides"
                >
                    <div
                        v-for="item in items"
                        :id="item.caption"
                        :key="item.slug"
                        ref="itemElsMobile"
                        class="slide"
                    >
                        <CmsImage
                            :alt="item.caption"
                            :data="item.image"
                            fit="cover"
                            :is-eager="hasEagerImage"
                            :sizes="['calc(100vw - 80px)']"
                        />
                    </div>
                </div>

                <ButtonRound
                    :aria-label="t('catalogue.carousel.button_prev')"
                    :disabled="isAtStart"
                    class="button-round left"
                    icon="angle-left"
                    @click="previousMobile"
                />

                <ButtonRound
                    :aria-label="t('catalogue.carousel.button_next')"
                    :disabled="isAtEnd"
                    class="button-round right"
                    icon="angle-right"
                    @click="nextMobile"
                />
            </div>

            <div class="caption-and-cta">
                <div class="caption">
                    {{ currentItem?.caption || items[0]?.caption }}
                </div>

                <ThButton
                    class="button-show-more"
                    color="var(--blue)"
                    @click="handleMobileCta"
                >
                    {{
                        data.modalButtonText
                            ? data.modalButtonText
                            : 'Design a ring like this'
                    }}
                </ThButton>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { useComponentGalleryWithEnquiryMobile } from './useComponentGalleryWithEnquiryMobile'
import { useComponentGalleryWithEnquiryDesktop } from './useComponentGalleryWithEnquiryDesktop'
import { useEnquiryFormStore } from '@/stores/enquiryForm'
import { useCurrentUrl } from '@/plugins/current-url.ts'

import {
    type ComponentGalleryWithEnquiryFragment,
    type EnquiryImageInput,
    type GalleryWithEnquiryCardFragment,
} from '@/graphql/api'

import Fixed from '@/components/layout/modal/Fixed.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import TransitionModal from '@/components/layout/modal/TransitionModal.vue'
import { getTimeout, getWindow } from '@/helpers/globals'
import CmsImage from '@/components/cms/CmsImage.vue'
import { useIsMobile } from '@/plugins/is-mobile.ts'

const props = defineProps<{
    data: ComponentGalleryWithEnquiryFragment
    hasEagerImage: boolean
}>()

const { t } = useI18n()

const items = toRef(() => props.data.items)

const route = useRoute()
const window = getWindow()

// The hash is updated through the History API instead of the router,
// so changing slides doesn't trigger a navigation (guards, scroll, re-render)
const hash = ref(route.hash)

watch(
    () => route.hash,
    (newHash) => {
        hash.value = newHash
    }
)

const setHash = (newHash: string) => {
    hash.value = newHash

    if (!window) {
        return
    }

    const url = new URL(window.location.href)
    url.hash = newHash

    window.history.replaceState(window.history.state, '', url.href)
}

const currentItem = computed({
    get: (): GalleryWithEnquiryCardFragment | undefined =>
        items.value.find((item) => `#${item.slug}` === hash.value),
    set: (item: GalleryWithEnquiryCardFragment | undefined) => {
        setHash(item ? `#${item.slug}` : '')
    },
})

const currentItemIndex = computed(() =>
    items.value.findIndex((item) => item.slug === currentItem.value?.slug)
)

const isAtStart = computed(
    () => currentItemIndex.value === 0 || currentItemIndex.value === -1
)
const isAtEnd = computed(
    () => currentItemIndex.value === items.value.length - 1
)

const isMobile = useIsMobile()

const {
    showMore,
    itemsDisplayed,
    isDesktopModalOpen,
    openDesktopModal,
    closeDesktopModal,
    desktopModalTransitionName,
    areAllItemsDislpayed,
    onEnter,
    onAfterEnter,
    previous,
    next,
} = useComponentGalleryWithEnquiryDesktop(
    isMobile,
    items,
    currentItem,
    currentItemIndex
)

const { containerMobile, itemElsMobile, previousMobile, nextMobile } =
    useComponentGalleryWithEnquiryMobile(
        isMobile,
        items,
        currentItem,
        currentItemIndex
    )

const { openEnquiryForm } = useEnquiryFormStore()
const currentUrl = useCurrentUrl()

const { setTimeout } = getTimeout() ?? { setTimeout: undefined }

// The buttons inside and outside the modal have different enquiry types.
const handleNonModalCta = () => {
    openEnquiryForm(props.data.type)
}

// The mobile cta uses the modal type as the desktop modal is not used on mobile.
const handleMobileCta = () => {
    handleModalCta()
}

const handleModalCta = () => {
    const image = currentItem.value?.image || items.value[0]?.image
    // currentUrl comes from the router, which doesn't know about the slide hash
    const source = new URL(currentUrl.value.href)
    source.hash = hash.value
    const sourceUrl = source.href

    closeDesktopModal()

    if (!image) {
        return
    }

    const enquiryImage: EnquiryImageInput | undefined = image
        ? {
              uri: image.url,
              name: image.alt || '',
          }
        : undefined

    // await the route change so the enq modal is not closed by the route change
    setTimeout?.(() => {
        openEnquiryForm(props.data.modalType, enquiryImage, sourceUrl)
    })
}
</script>

<style lang="scss" scoped>
.component-gallery-with-enquiry {
    .desktop {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        padding: 20px;

        .items {
            max-width: 1140px;
            display: flex;
            flex-wrap: wrap;
            gap: 20px;

            .item {
                position: relative;
                flex: 1 1 40%;

                @include min-width(768) {
                    flex: 1 1 30%;
                }

                .overlay {
                    display: flex;
                    opacity: 0;
                    position: absolute;
                    inset: 0;
                    padding: 30px;
                    align-items: center;
                    background-color: rgba($black, 0.6);
                    backdrop-filter: blur(5px);
                    color: $white;
                    font-size: 20px;
                    text-align: center;
                    cursor: pointer;
                    user-select: none;
                    line-height: 1.2em;

                    &:hover {
                        opacity: 1;
                    }
                }
            }
        }

        .buttons {
            margin-top: 20px;
            display: flex;
            gap: 20px;
        }
    }

    .mobile {
        position: relative;
        width: 100%;

        .carousel-container {
            position: relative;

            .slides {
                position: relative;
                display: flex;
                overflow-y: hidden;
                overflow-x: scroll;
                scroll-snap-type: x mandatory;
                scroll-padding: 0 40px;
                padding: 0 40px;
                -ms-overflow-style: none; /* IE and Edge */
                scrollbar-width: none; /* Firefox */

                &::-webkit-scrollbar {
                    display: none;
                }

                .slide {
                    user-select: none;
                    scroll-snap-align: start;
                    flex: 1 0 100%;
                    margin-right: 5px;
                }
            }

            .button-round {
                position: absolute;
                z-index: 3;
                top: calc(50% - 25px);

                &.disabled {
                    display: none;
                }

                &.left {
                    left: 10px;
                }

                &.right {
                    right: 15px;
                }
            }
        }

        .caption-and-cta {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 16px;

            .caption {
                margin-bottom: 8px;
                min-height: 109px;
                padding: 16px;
                text-align: center;
            }
        }
    }
}

.desktop-modal {
    position: relative;
    inset: 0;
    width: 100%;
    height: 100%;
    padding: 20px;
    background-color: $blue;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: $white;

    .image-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        max-width: 65%;
        max-height: 65%;
        margin-bottom: 48px;
    }

    .caption {
        margin-bottom: 24px;
        max-width: 480px;
        min-height: 80px;
        text-align: center;
    }

    .button-close {
        @include close-button;
    }

    .button-navigation {
        position: absolute;
        z-index: 3;
        top: calc(40% - 25px);

        &.disabled {
            display: none;
        }

        &.previous {
            left: 10px;
        }

        &.next {
            right: 15px;
        }
    }
}

.slide-enter-active,
.slide-leave-active,
.slide-reverse-enter-active,
.slide-reverse-leave-active {
    transition: all 165ms var(--timing-function-easing-accelerate);
}

.slide-enter-from,
.slide-reverse-leave-to {
    transform: translate(100%);
    opacity: 0;
}

.slide-leave-to,
.slide-reverse-enter-from {
    transform: translate(-100%);
    opacity: 0;
}
</style>
